import { TranslationKeys } from 'localization';

import { hasCoverageHoursChanges } from './coverageHoursDirtyState';
import { CoverageHoursPair } from './coverageHoursPairMapper';
import { CoverageHoursPairTarget } from './useCoverageHoursPair';
import {
  ALL_ISSUE_TYPES_VALUE,
  NONE_ISSUE_TYPE_VALUE,
} from './coverageHoursPanelUtils';
import { TeamCoverageEntry } from './coverageHoursTeamUtils';
import { CoverageHoursOptionsStatus } from './useCoverageHoursOptions';

export enum CoverageHoursPanelMode {
  Create = 'create',
  Edit = 'edit',
}

export type CoverageHoursPairIdentity = {
  routingId: string | null;
  category: string;
  issue: string | null;
};

const TITLE_KEYS: Record<CoverageHoursPanelMode, TranslationKeys> =
  Object.freeze({
    [CoverageHoursPanelMode.Create]:
      'web.admin.serviceRequest.settings.coverageHours.create.title',
    [CoverageHoursPanelMode.Edit]:
      'web.admin.serviceRequest.settings.coverageHours.edit.title',
  });

const SAVE_ERROR_KEYS: Record<CoverageHoursPanelMode, TranslationKeys> =
  Object.freeze({
    [CoverageHoursPanelMode.Create]:
      'web.admin.serviceRequest.settings.coverageHours.create.saveError',
    [CoverageHoursPanelMode.Edit]:
      'web.admin.serviceRequest.settings.coverageHours.edit.saveError',
  });

const PAIR_LOAD_ERROR_KEY: TranslationKeys =
  'web.admin.serviceRequest.settings.coverageHours.edit.loadError';

const OPTIONS_LOAD_ERROR_KEY: TranslationKeys =
  'web.admin.serviceRequest.settings.coverageHours.error.load';

export type CoverageHoursPanelDefaults = {
  category: string | null;
  issueType: string;
};

export function resolveCoverageHoursPanelDefaults(
  mode: CoverageHoursPanelMode,
  pair?: CoverageHoursPairIdentity
): CoverageHoursPanelDefaults {
  if (mode === CoverageHoursPanelMode.Create) {
    return { category: null, issueType: ALL_ISSUE_TYPES_VALUE };
  }

  return {
    category: pair?.category ?? null,
    issueType: pair?.issue ?? NONE_ISSUE_TYPE_VALUE,
  };
}

export type CoverageHoursEditSession = {
  mode: CoverageHoursPanelMode;
  initialTeams: TeamCoverageEntry[];
  pair?: CoverageHoursPairIdentity;
  loadError: boolean;
};

const NO_TEAMS: TeamCoverageEntry[] = [];

export function resolveCoverageHoursEditSession(
  target: CoverageHoursPairTarget | null,
  loaded: CoverageHoursPair | null,
  loadFailed: boolean
): CoverageHoursEditSession {
  if (!target) {
    return {
      mode: CoverageHoursPanelMode.Create,
      initialTeams: NO_TEAMS,
      loadError: false,
    };
  }

  return {
    mode: CoverageHoursPanelMode.Edit,
    initialTeams: loaded?.teams ?? NO_TEAMS,
    pair: {
      routingId: loaded?.routingId ?? null,
      category: target.category,
      issue: target.issue,
    },
    loadError: loadFailed,
  };
}

export type CoverageHoursPanelViewModelArgs = {
  mode: CoverageHoursPanelMode;
  categoryValue: string | null;
  routingId?: string | null;
  initialTeams: TeamCoverageEntry[];
  savedTeams: TeamCoverageEntry[];
  saveError: boolean;
  loadError?: boolean;
  optionsStatus: CoverageHoursOptionsStatus;
  optionsError: boolean;
  hasTeamFormConflict: boolean;
  isTeamFormOpen: boolean;
  isSaving: boolean;
};

export type CoverageHoursPanelViewModel = {
  titleKey: TranslationKeys;
  saveErrorKey: TranslationKeys | null;
  loadErrorKey: TranslationKeys | null;
  hasUnsavedChanges: boolean;
  canSave: boolean;
};

type SavableArgs = Pick<
  CoverageHoursPanelViewModelArgs,
  | 'mode'
  | 'categoryValue'
  | 'routingId'
  | 'savedTeams'
  | 'loadError'
  | 'optionsStatus'
  | 'hasTeamFormConflict'
  | 'isTeamFormOpen'
  | 'isSaving'
>;

function isSavable({
  mode,
  categoryValue,
  routingId,
  savedTeams,
  loadError,
  optionsStatus,
  hasTeamFormConflict,
  isTeamFormOpen,
  isSaving,
}: SavableArgs): boolean {
  if (!categoryValue || savedTeams.length === 0 || isSaving) {
    return false;
  }

  if (optionsStatus !== 'success' || hasTeamFormConflict || isTeamFormOpen) {
    return false;
  }

  if (loadError) {
    return false;
  }

  // An edit submits against one routing. Without it there is nothing to write
  // to, and the panel's own guard turns Save into a click that reports nothing.
  return mode === CoverageHoursPanelMode.Create || Boolean(routingId);
}

export function createCoverageHoursPanelViewModel({
  mode,
  categoryValue,
  routingId,
  initialTeams,
  savedTeams,
  saveError,
  loadError,
  optionsStatus,
  optionsError,
  hasTeamFormConflict,
  isTeamFormOpen,
  isSaving,
}: CoverageHoursPanelViewModelArgs): CoverageHoursPanelViewModel {
  const loadErrorKey = loadError
    ? PAIR_LOAD_ERROR_KEY
    : (optionsError && OPTIONS_LOAD_ERROR_KEY) || null;

  return {
    titleKey: TITLE_KEYS[mode],
    saveErrorKey: saveError ? SAVE_ERROR_KEYS[mode] : null,
    loadErrorKey,
    hasUnsavedChanges: hasCoverageHoursChanges(initialTeams, savedTeams),
    canSave: isSavable({
      mode,
      categoryValue,
      routingId,
      savedTeams,
      loadError,
      optionsStatus,
      hasTeamFormConflict,
      isTeamFormOpen,
      isSaving,
    }),
  };
}
